/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * Svelte attachment factory
 * @param {DOMElement|null} [scrollContainer] - scroll container
 * @returns {Function} Svelte attachment
 * @example <div {@attach createVisibilityObserver(container)}></div>
 */
export default function createVisibilityObserver(scrollContainer) {
    return node => visibilityObserver(node, scrollContainer);
}

/**
 * Svelte attachment which adds IntersectionObserver for the element.
 * Fires event when node comes into view, or when part of it becomes hidden.
 * Can be used to detect if parent element is fully scrolled up or down.
 * @param {DOMElement} node - the node implementing this action
 * @param {DOMElement|null} [scrollContainer] - scroll container:
 *    set to `null` if scroll is on document body;
 *    set to 'undefined' (not specified) if no need to add observer yet, or to remove existing one
 * @returns {Function} destroy hook
 * @fires isVisible - e.detail is Boolean - if element is visible or not
 */
function visibilityObserver(node, scrollContainer) {
    // rootMargin is added to avoid browser rounding issues:
    // sometimes parent has `bottom = 342`, but node has `bottom = 342.39`, and intersection is not detected
    const observerOptions = {
        threshold: 0,
        rootMargin: '1px 1px 1px 1px'
    };
    let observer;

    // runs on node mount, and whenever node or scrollContainer changed
    $effect(() => {
        const newRoot = scrollContainer === null ? document.body : scrollContainer;
        if (newRoot !== observerOptions.root) {
            observerOptions.root = newRoot;
            if (observer) {
                observer.disconnect();
            }
            observer = createObserver(node, observerOptions);
        }
    });

    return function destroy() {
        if (observer) {
            observer.disconnect();
        }
    };
}

function createObserver(node, observerOptions) {
    if (typeof observerOptions.root === 'undefined') {
        return null;
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const isVisible = entry.isIntersecting;
            node.dispatchEvent(new CustomEvent('isVisible', { detail: isVisible }));
        });
    }, observerOptions);

    observer.observe(node);
    return observer;
}
